import { getLocalizedStringValue } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { MetricUnit } from 'shared/types/goal-metric';
import { GoalProgressHistoryFormFields } from 'shared/types/goal-progress-form';
import { ID } from 'shared/types/id';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';
import { useTheme } from 'styled-components';

import { GoalProgressHistoryEntry } from './goal-progress-history-entry';
import * as Styled from './goal-progress-history-form.style';

export type GoalProgressHistoryFormProps = {
  entries: GoalProgressHistoryFormFields['entries'];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  unit?: MetricUnit;
  startValue?: number;
  target?: number;
  onSubmit: (entries: GoalProgressHistoryFormFields['entries']) => void;
  onClose: () => void;
  onAdd?: () => void;
};

export const GoalProgressHistoryForm: React.FC<
  GoalProgressHistoryFormProps
> = ({
  entries,
  dateFormat,
  weekStartsOn,
  unit,
  startValue,
  target,
  onSubmit,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const theme = useTheme();
  const [newEntries, setNewEntries] = useState(entries);
  const localizedSymbol = unit?.symbol
    ? getLocalizedStringValue(unit.symbol, locale)
    : undefined;

  const unitFormat = unit
    ? unit.format ??
      `${metricUnitFormatBase} ${getLocalizedStringValue(unit.name, locale)}`
    : metricUnitFormatBase;

  const separators = useNumberFormat();
  const formattedTarget =
    isNumber(target) &&
    getLocalizedStringValue(unitFormat, locale).replace(
      metricUnitFormatBase,
      formatNumberShort(target!, separators.decimal),
    );

  const sortedEntries = useMemo(
    () =>
      newEntries.toSorted(
        (entryA, entryB) => entryB.date.getTime() - entryA.date.getTime(),
      ),
    [newEntries],
  );

  const submitForm = useCallback(() => {
    onSubmit(sortedEntries);
    onClose();
  }, [onClose, onSubmit, sortedEntries]);

  const _onAdd = () => {
    onSubmit(sortedEntries);
    onAdd?.();
  };

  const updateEntryValue = async (id: ID, value: number) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return;
    }

    setNewEntries((prevEntries) => {
      const _entries = structuredClone(prevEntries);
      const entryIndex = _entries.findIndex((entry) => entry.id === id);
      if (entryIndex !== -1) {
        _entries[entryIndex].value = Math.round(value * 100) / 100;
      }
      return _entries;
    });
  };

  const updateEntryDate = async (id: ID, date: Timestamp) =>
    setNewEntries((prevEntries) => {
      const _entries = structuredClone(prevEntries);
      const entryIndex = _entries.findIndex((entry) => entry.id === id);

      if (entryIndex !== -1) {
        const time = new Date();
        date.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
        _entries[entryIndex].date = date;
      }

      return _entries;
    });

  const deleteEntry = (id: ID) => {
    setNewEntries((prevEntries) =>
      prevEntries.filter((entry) => entry.id !== id),
    );
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return;
    }

    const activeElement = document.activeElement as HTMLElement;
    if (!activeElement) {
      // without focus on something the normal flow should continue
      return;
    }

    const elementName = activeElement.getAttribute('name') ?? '';

    if (elementName.startsWith('entries.')) {
      event.preventDefault();
      event.stopPropagation();
      // when it's one of the entries, blurring it will trigger the functionality needed.
      activeElement.blur();
      return;
    }
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') {
        return;
      }
      const elementName = document.activeElement?.getAttribute('name') ?? '';

      if (elementName.startsWith('entries.')) {
        return;
      }

      e.preventDefault();
      submitForm();
    };

    window.addEventListener('keydown', onEnter);

    return () => window.removeEventListener('keydown', onEnter);
  }, [submitForm]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('forms.goal-progress.title')}
        </Typography>
        {!!formattedTarget && (
          <Styled.Description>
            {t('forms.goal-progress.description', { target: formattedTarget })}
          </Styled.Description>
        )}
      </Styled.Header>

      <Styled.Body>
        {!!sortedEntries.length && (
          <Styled.ScrollContainer>
            <Styled.Table>
              <Styled.TableHeader>
                <Styled.TableRow>
                  <Styled.TableHeadCell>
                    <Typography variant="buttonSmall">
                      {t('forms.goal-progress.fields.date.label')}
                    </Typography>
                  </Styled.TableHeadCell>
                  <Styled.TableHeadCell $alignRight>
                    <Typography variant="buttonSmall">
                      {t('forms.goal-progress.fields.increment.label')}
                    </Typography>
                  </Styled.TableHeadCell>
                  <Styled.TableHeadCell $alignRight>
                    <Typography variant="buttonSmall">
                      {t('forms.goal-progress.fields.total.label')}
                    </Typography>
                  </Styled.TableHeadCell>
                  <Styled.TableHeadCell $isInteraction></Styled.TableHeadCell>
                </Styled.TableRow>
              </Styled.TableHeader>

              <Styled.TableBody>
                {sortedEntries.map((entry) => (
                  <GoalProgressHistoryEntry
                    key={entry.id}
                    entry={entry}
                    sortedEntries={sortedEntries}
                    dateFormat={dateFormat}
                    weekStartsOn={weekStartsOn}
                    startValue={startValue ?? 0}
                    localizedSymbol={localizedSymbol}
                    theme={theme}
                    onUpdateValue={updateEntryValue}
                    onUpdateDate={updateEntryDate}
                    onDelete={deleteEntry}
                    onKeyDown={onKeyDown}
                  />
                ))}
              </Styled.TableBody>
            </Styled.Table>
          </Styled.ScrollContainer>
        )}
      </Styled.Body>

      <Styled.Footer>
        {!!onAdd && (
          <Button
            variant={Variants.Text}
            size={Sizes.Medium}
            onClick={_onAdd}
            startIcon={Plus}
            color={Colors.Inherit}
            type="button"
          >
            {t('forms.goal-progress.buttons.add-entry.label')}
          </Button>
        )}

        <Styled.Interactions>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.White}
            onClick={onClose}
            type="button"
          >
            {t('forms.goal-progress.buttons.cancel.label')}
          </Button>
          <Button size={Sizes.Medium} type="submit">
            {t('forms.goal-progress.buttons.save.label')}
          </Button>
        </Styled.Interactions>
      </Styled.Footer>
    </Styled.Form>
  );
};
